import { normalizeClientIvaCondition } from './clientFiscal.js';

function normalizeString(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function pickFirst(...values) {
  return values.find(value => String(value || '').trim()) || '';
}

function formatClientCondition(rawCondition) {
  return normalizeClientIvaCondition(rawCondition);
}

function resolveClientAddress(client) {
  const domicilios = Array.isArray(client?.domicilios) ? client.domicilios : [];
  const preferred =
    domicilios.find(domicilio => domicilio?.esDefault) ||
    domicilios.find(domicilio => normalizeString(domicilio?.tipo).includes('fiscal')) ||
    domicilios.find(domicilio => normalizeString(domicilio?.tipo).includes('comercial')) ||
    domicilios[0];

  return pickFirst(preferred?.direccion, client?.direccion);
}

function resolveClientDocument(client) {
  return pickFirst(client?.cuit, client?.doc, client?.documento);
}

export function normalizeClientForManualInvoice(client) {
  if (!client) {
    return null;
  }

  const razonSocial = pickFirst(client?.razonSocial, client?.razon, client?.nombre, client?.nombreFantasia);
  const condicionIva = formatClientCondition(
    pickFirst(client?.condicionIVA, client?.condicionIva, client?.iva, client?.condicion_iva),
  );
  const direccion = resolveClientAddress(client);
  const telefono = pickFirst(client?.telefono, client?.telefono1, client?.telefonoFijo, client?.celular);
  const localidad = pickFirst(client?.localidad?.nombre, client?.localidadNombre);
  const provincia = pickFirst(client?.provincia?.nombre, client?.provinciaNombre);
  const codigoPostal = pickFirst(client?.codigoPostal, client?.cp);

  return {
    ...client,
    id: client?.id,
    razonSocial,
    razon: razonSocial,
    cuit: resolveClientDocument(client),
    condicionIVA: condicionIva,
    iva: condicionIva,
    direccion,
    telefono,
    localidadNombre: localidad,
    provinciaNombre: provincia,
    codigoPostal,
  };
}

export function resolveVoucherTypeForClient(client) {
  const normalizedClient = normalizeClientForManualInvoice(client);
  const condition = normalizeString(normalizedClient?.condicionIVA);

  if (!condition) {
    return {
      voucherType: '',
      locked: true,
      reason: 'Falta la condición IVA del cliente para resolver el comprobante.',
    };
  }

  if (condition === 'responsable inscripto' || condition === 'ri' || condition === 'iva responsable inscripto') {
    return {
      voucherType: 'Factura A',
      locked: true,
      reason: 'Cliente inscripto: corresponde comprobante A.',
    };
  }

  if (
    ['monotributo', 'monotributista', 'mt', 'mo', 'consumidor final', 'cf', 'exento', 'ex', 'responsable no inscripto', 'rni'].includes(
      condition,
    )
  ) {
    return {
      voucherType: 'Factura B',
      locked: true,
      reason: 'Cliente no alcanzado como inscripto para A: corresponde comprobante B.',
    };
  }

  return {
    voucherType: '',
    locked: true,
    reason: 'No se pudo resolver automáticamente el comprobante con la condición fiscal actual.',
  };
}

export function evaluateManualInvoiceClient(client) {
  const normalizedClient = normalizeClientForManualInvoice(client);
  const voucher = resolveVoucherTypeForClient(normalizedClient);
  const missingCriticalFields = [];

  if (!normalizedClient?.razonSocial) missingCriticalFields.push('razón social');
  if (!normalizedClient?.cuit) missingCriticalFields.push('CUIT/DNI');
  if (!normalizedClient?.condicionIVA) missingCriticalFields.push('condición IVA');
  if (!normalizedClient?.direccion) missingCriticalFields.push('domicilio');
  if (!voucher.voucherType) missingCriticalFields.push('tipo de comprobante');

  return {
    client: normalizedClient,
    voucherType: voucher.voucherType,
    voucherLocked: voucher.locked,
    voucherReason: voucher.reason,
    missingCriticalFields,
    hasCriticalIssues: missingCriticalFields.length > 0,
  };
}
